import { Component, OnInit, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { MenuItem, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';
import { BoatFilterListWithCustomerDTO } from 'src/app/models/dtos/boat-filter-list-with-customerDTO';
import { CustomerFilterListDTO } from 'src/app/models/dtos/customerFilterListDTO';
import { ProductFilterListDTO } from 'src/app/models/dtos/productFilterListDTO';
import { ServicePackageOrderFilter } from 'src/app/models/dtos/service-package-order-filter';
import { ServicePackageOrder } from 'src/app/models/service-package-order.model';
import { BoatWithPackagesDTO } from 'src/app/models/boat-with-packages.model';
import { BoatService } from 'src/app/services/boat.service';
import { CustomerService } from 'src/app/services/customer.service';
import { PackageServiceService } from 'src/app/services/package-service.service';
import { ServicePackageOrderService } from 'src/app/services/service-package-order.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { StorageUtil } from 'src/app/utils/storage.util';
import { ServicePackageOrderFormComponent } from '../../forms/package-services/service-package-order-form/service-package-order-form.component';
import { EarlyClosureDialogComponent } from './early-closure-dialog/early-closure-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { PackageServiceBalanceDTO } from 'src/app/models/dtos/package-service-balance-DTO';
import { PackageConsumptionHistoryRequestDTO } from 'src/app/models/dtos/package-consumption-history-request-DTO';
import { ConsumptionHistoryDialogComponent } from './consumption-history-dialog/consumption-history-dialog.component';

@Component({
  selector: 'app-package-manager',
  templateUrl: './package-manager.component.html',
  styleUrls: ['./package-manager.component.scss']
})
export class PackageManagerComponent implements OnInit {
  @ViewChild('menu') menu: any;
  
  packageFilter: ServicePackageOrderFilter = new ServicePackageOrderFilter();

  boats: SelectItem[] = [];
  customers: SelectItem[] = [];
  packageServices: SelectItem[] = [];
  statusOptions: SelectItem[] = [];

  servicePackageOrders: BoatWithPackagesDTO[] = [];
  totalRecords: number = 0;
  loading: boolean = false;
  hasSearched: boolean = false;
  noResults: boolean = false;
  
  menuItems: MenuItem[] = [];
  selectedPackage: any = null;

  constructor(
    private boatService: BoatService,
    private customerService: CustomerService,
    private packageServiceService: PackageServiceService,
    private servicePackageOrderService: ServicePackageOrderService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil,
    private dialog: DialogService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
    this.initializeStatusOptions();
  }

  loadInitialData(): void {
    this.spinner.show();
    const boatsObservable = this.loadBoatsData();
    const customersObservable = this.loadCustomersData();
    const packageServicesObservable = this.loadPackageServicesData();

    Promise.all([
      boatsObservable,
      customersObservable,
      packageServicesObservable
    ])
    .then(() => {
      this.spinner.hide();
    })
    .catch((error) => {
      console.error('Erro ao carregar dados iniciais:', error);
      this.messageUtil.generateMessage('error', 'Erro', 'Erro ao carregar dados iniciais');
      this.spinner.hide();
    });
  }

  loadBoatsData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.boatService.getBoatFilterListWithCustomer().subscribe({
        next: (data: BoatFilterListWithCustomerDTO[]) => {
          this.boats = data.map(boat => ({
            label: boat.name,
            value: boat.id
          }));
          resolve();
        },
        error: (error) => {
          console.error('Erro ao carregar embarcações:', error);
          this.messageUtil.generateMessage('error', 'Erro', 'Erro ao carregar embarcações');
          reject(error);
        }
      });
    });
  }

  // EBUG-280: filtro de cliente migrado para app-customer-search-field (busca sob demanda);
  // não carrega mais a lista completa de clientes.
  loadCustomersData(): Promise<void> {
    return Promise.resolve();
  }

  loadPackageServicesData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.packageServiceService.findAll().subscribe({
        next: (data) => {
          this.packageServices = data.map(packageService => ({
            label: packageService.name,
            value: packageService.id
          }));
          resolve();
        },
        error: (error) => {
          console.error('Erro ao carregar pacotes de serviço:', error);
          this.messageUtil.generateMessage('error', 'Erro', 'Erro ao carregar pacotes de serviço');
          reject(error);
        }
      });
    });
  }

  find(): void {
    this.spinner.show();
    this.hasSearched = true;

    // Processa o filtro de status
    this.processStatusFilter();

    if (this.packageServices.length === 0) {
      setTimeout(() => {
        this.executeSearch();
      }, 3000);
    } else {
      this.executeSearch();
    }
  }

  executeSearch(): void {
    this.servicePackageOrderService.findByMarinaIdWithPagination(this.packageFilter).subscribe({
      next: (result) => {
        this.servicePackageOrders = result.content;
        this.totalRecords = result.totalElements;
        this.noResults = this.servicePackageOrders.length === 0;

        this.spinner.hide();
      },
      error: (error) => {
        console.error('Erro ao buscar pacotes de serviço:', error);
        this.spinner.hide();
      }
    });
  }

  clear(): void {
    this.packageFilter = new ServicePackageOrderFilter();
    // Reseta o filtro de status para 'Todos'
    this.packageFilter.packageStatus = ['all'];
  }

  onChangePage(event: any): void {
    this.packageFilter.page = event.first / event.rows;
    this.packageFilter.size = event.rows;
    this.spinner.show();
    this.hasSearched = true;
    this.executeSearch();
  }

  getExcedentTotal(pacote: any): number {
    if (!pacote.itens || !Array.isArray(pacote.itens)) {
      return 0;
    }

    return pacote.itens.reduce((total: number, item: any) => {
      return total + (item.qtdUsed > item.quantity ? (item.qtdUsed - item.quantity) * item.unitValue : 0);
    }, 0);
  }

  getTotalGeral(pacote: any): number {
    if (!pacote) {
      return 0;
    }

    return pacote.valor + this.getExcedentTotal(pacote);
  }

  getTotalGeralComDesconto(pacote: any): number {
    if (!pacote) {
      return 0;
    }
    return pacote.valor - pacote.desconto;
  }



  getStatusIcon(status: string): string {
    switch (status) {
      case 'active': return 'pi pi-check-circle';
      case 'canceled': return 'pi pi-times-circle';
      case 'ended': return 'pi pi-calendar-times';
      default: return 'pi pi-question-circle';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Ativo';
      case 'canceled': return 'Cancelado';
      case 'ended': return 'Encerrado';
      default: return 'Desconhecido';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active': return 'status-active';
      case 'canceled': return 'status-canceled';
      case 'ended': return 'status-ended';
      default: return 'status-unknown';
    }
  }

  initializeStatusOptions(): void {
    this.statusOptions = [
      { label: 'Todos', value: 'all' },
      { label: 'Ativo', value: 'active' },
      { label: 'Cancelado', value: 'canceled' },
      { label: 'Encerrado', value: 'ended' }
    ];
    
    // Seleciona 'Todos' por padrão
    this.packageFilter.packageStatus = ['all'];
  }

  processStatusFilter(): void {
    // Se 'Todos' estiver selecionado, limpa o filtro de status
    if (this.packageFilter.packageStatus.includes('all')) {
      this.packageFilter.packageStatus = [];
    }
  }

  openServicePackageOrderDialog(): void {
    const ref = this.dialog.open(ServicePackageOrderFormComponent, {
      header: this.translateService.instant('NEW-SERVICE-PACKAGE-ORDER'),
      width: '700px',
      data: {
        servicePackageOrder: null,
        isEdit: false
      }
    });

    ref.onClose.subscribe((result) => {
      if (result) {
        this.find();
      }
    });
  }

  changeSelectedPackage(barco: any, pacote: any): void {
    this.selectedPackage = { barco, pacote };
    this.buildMenuItems();
  }

  buildMenuItems(): void {
    const menuItems = [{
      label: 'Antecipar fim de vigência',
      icon: 'pi pi-calendar',
      command: () => this.openAnteciparFechamentoDialog()
    },
    {
      label: 'Histórico de Utilização',
      icon: 'pi pi-history',
      command: () => this.openConsumptionHistory()
    }];

    if (this.selectedPackage && this.selectedPackage.pacote && 
        this.selectedPackage.pacote.packageStatus === 'active' && 
        this.hasPackageNotBeenConsumed(this.selectedPackage.pacote)) {
      menuItems.push({
        label: 'Cancelar Pacote de Serviço',
        icon: 'pi pi-times',
        command: () => this.openCancelPackageDialog()
      });
    }

    this.menuItems = menuItems;
  }

  hasPackageNotBeenConsumed(pacote: any): boolean {
    if (!pacote.itens || pacote.itens.length === 0) {
      return false;
    }

    return pacote.itens.every((item: any) => {
      return item.quantity === item.remainingBalance && 
             (item.qtdUsed === 0 || item.qtdUsed === undefined || item.qtdUsed === null);
    });
  }

  openCancelPackageDialog(): void {
    if (!this.selectedPackage) return;

    this.messageUtil.confirmMessage(
      this,
      'Cancelar Pacote de Serviço',
      'Tem certeza que deseja cancelar este pacote de serviço?',
      'Esta ação não pode ser desfeita.'
    );
  }

  confirmationDialog(): void {
    this.cancelPackage();
  }

  cancelPackage(): void {
    if (!this.selectedPackage || !this.selectedPackage.pacote) return;

    const packageBoatId = this.selectedPackage.pacote.idPacote;
    this.spinner.show();

    this.servicePackageOrderService.cancelPackageBoat(packageBoatId).subscribe({
      next: () => {
        this.spinner.hide();
        this.messageUtil.generateMessage('success', 'Sucesso', 'Pacote cancelado com sucesso');
        this.find(); // Refresh the data
      },
      error: (error) => {
        this.spinner.hide();
        this.messageUtil.generateMessage('error', 'Erro', 'Erro ao cancelar pacote');
        console.error('Error canceling package:', error);
      }
    });
  }

  openAnteciparFechamentoDialog(): void {
    if (!this.selectedPackage) return;

    const ref = this.dialog.open(EarlyClosureDialogComponent, {
      header: 'Antecipar fim de vigência do Pacote',
      width: '500px',
      data: {
        pacote: this.selectedPackage.pacote,
        dataFimValidade: this.selectedPackage.pacote.dataFimValidade,
        packageBoatId: this.selectedPackage.pacote.idPacote
      }
    });

    ref.onClose.subscribe((result) => {
      if (result) {
        // Aqui você pode chamar o endpoint para salvar a data antecipada
        console.log('Data selecionada para antecipação:', result);
        this.messageUtil.generateMessage('success', 'Sucesso', 'Fechamento antecipado com sucesso');
      }
    });
  }

  openConsumptionHistory(): void {
    if (!this.selectedPackage) return;

    console.log('daniel' ,this.selectedPackage.pacote);
    
    const marinaId = StorageUtil.getMarina()?.id;
    
    if (!marinaId) {
      this.messageUtil.generateMessage('error', 'Erro', 'Marina ID não encontrado');
      return;
    }

    const payload: PackageConsumptionHistoryRequestDTO = new PackageConsumptionHistoryRequestDTO();
    payload.boatId = this.selectedPackage.barco.id;
    payload.boatNome = this.selectedPackage.barco.nome;
    payload.customerId = this.selectedPackage.barco.customerId || 0;
    payload.customerName = this.selectedPackage.barco.customerName || '';
    payload.packageId = this.selectedPackage.pacote.idPacote;
    payload.packageName = this.selectedPackage.pacote.nomePacote;

    this.spinner.show();
    this.packageServiceService.getPackageConsumptionHistory(payload, marinaId)
      .subscribe({
        next: (consumptionHistory) => {
          this.spinner.hide();
          
          
          this.dialog.open(ConsumptionHistoryDialogComponent, {
            header: 'Histórico de Consumo de Pacote',
            width: '52vw',
            height: '80vh',
            data: {
              consumptionHistory: consumptionHistory,
              packageName: this.selectedPackage?.pacote.nomePacote,
              boatName: this.selectedPackage?.barco.nome
            }
          });
        },
        error: (error) => {
          this.spinner.hide();
          console.error('Erro ao carregar histórico de consumo:', error);
          this.messageUtil.generateMessage('error', 'Erro', 'Erro ao carregar histórico de consumo');
        }
      });
  }
}
